import { useCallback, useEffect, useState } from "react";

/**
 * *
 * @type {{isFetching: boolean, requests: {next: {}, current: {requestType: null, func: null, addedTimestamp: null}}}}
 */
const initialState = {
	requests: {
		current: {
			requestType: null,
			func: null,
			addedTimestamp: null,
		},
		next: {},
	},
	isFetching: false
};

/*
 * This hook is meant to prevent simultaneous requests (+ the resulting concurrent modification) to an endpoint
 * that may arise when calling the endpoint in a useEffect or something similar
 *
 * addRequest expects a promise producer, meaning a function that results in a being returned
 * the purpose of using promise producers is to prevent the .then from
 * being called until it can be accessed in this component
 */
export const useRequestChain = () => {
	const [ requestsState, setRequestsState ] = useState(initialState);

	useEffect(() => {
		if (!requestsState.isFetching && Object.keys(requestsState.requests.next).length >= 1) {
			const requestsByDate = Object.entries(requestsState.requests.next)
				.map(([key, value]) => ({ ...value, requestType: key }))
				.sort((f, s) => {
					if (f.requestType === "save-form") {
						return -1;
					}
					return f.addedTimestamp - s.addedTimestamp;
				});

			const nextRequests = requestsByDate.slice(1, requestsByDate.length).reduce((acc , v) => {
				acc[v.requestType] = {
					func: v.func,
					addedTimestamp: v.addedTimestamp,
					additionalInfo: v.additionalInfo,
				};
				return acc;
			}, {});

			const currentRequest = requestsByDate[0];

			const newState = {
				requests: {
					next: nextRequests,
					current: currentRequest,
				},
				isFetching: true
			};

			setRequestsState(newState);

			currentRequest.func().then(() => {
				setRequestsState( requestsState => ({
					requests: {
						next: requestsState.requests.next,
						current: null,
					},
					isFetching: false,
				}));
			});
		}
	}, [ requestsState ]);

	/**
	 * @type {function(name:string, function():Promise): void | Error}
	 */
	const addRequest = useCallback((name = "", requestProducer, additionalInfo) => {
		if (!name) {
			throw new Error("addRequest: name is required");
		}

		setRequestsState((requestsState) => {
			return {
				...requestsState,
				requests: {
					next: {
						...requestsState.requests.next,
						[name]: {
							func: requestProducer,
							additionalInfo,
							addedTimestamp: new Date().getTime()
						},
					},
					current: requestsState.requests.current,
				},
			};
		});
		return name;
	}, []);

	const clearByName = (name) => {
		setRequestsState((requestsState) => {
			const newRequests = { ...requestsState.requests.next };
			delete newRequests[name];

			return {
				...requestsState,
				requests: {
					next: newRequests,
					current: requestsState.requests.current,
				},
			};
		})
	};

	return {
		addRequest,
		clearByName,
		isFetching: requestsState.isFetching,
		requests: requestsState.requests,
	};
};
